import { Container, Group, Stack } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { showNotification } from '@mantine/notifications';
import type { VersionHistoryOut } from '@repo/api-codegen';
import {
	fetchApiListVersionHistories,
	useApiRevertToHistory,
} from '@repo/api-codegen';
import { trackEvent } from '@repo/common/analytics';
import { useInfinitePagination } from '@repo/common/components/ActivityLog/ResourceLogs/hooks';
import { Banner, Button, Text, Title } from '@repo/foundations';
import { isNil } from 'lodash-es';
import { useCallback, useEffect, useState } from 'react';
import { Virtuoso } from 'react-virtuoso';

import { secodaEntitiesQueryKeyFactory } from '../../api/hooks/secodaEntity/constants';
import { useSuspenseFeatureAccess } from '../../api/hooks/workspace/useSuspenseFeatureAccess';
import queryClient from '../../api/queryClient';
import { RichEditor } from '../RichEditor';
import { SnapshotModalBody } from './SnapshotModalBody';
import { SnapshotModalContentUpgrade } from './SnapshotModalContentUpgrade';
import { SnapshotProperties } from './SnapshotProperties';
import { VersionHistoryButton } from './VersionHistoryButton/VersionHistoryButton';
import { VersionHistoryRestoreModal } from './VersionHistoryRestoreModal/VersionHistoryRestoreModal';

export interface SnapshotModalContentProps {
	resourceId: string;
	initialSelectedId?: string;
	onClose: () => void;
}

export function SnapshotModalContent({
	resourceId,
	initialSelectedId,
	onClose,
}: SnapshotModalContentProps) {
	const {
		page,
		setPage,
		fetchedPages,
		setFetchedPages,
		hasNextPage,
		setHasNextPage,
		loading,
		setLoading,
	} = useInfinitePagination();

	const [selectedId, setSelectedId] = useState(initialSelectedId);
	const [versionHistories, setVersionHistories] = useState<VersionHistoryOut[]>(
		[]
	);
	const [opened, { open, close }] = useDisclosure(false);

	const loadMore = useCallback(async () => {
		if (loading || fetchedPages.has(page) || !hasNextPage) return;
		setLoading(true);

		const response = await fetchApiListVersionHistories({
			queryParams: {
				resource_id: resourceId,
				page: page,
			},
		});

		setVersionHistories((prev) => [...prev, ...response.results]);

		setPage(page + 1);
		setFetchedPages((prev) => new Set(prev).add(page));
		setHasNextPage(!isNil(response.meta.next_page));
		setLoading(false);
	}, [
		loading,
		fetchedPages,
		page,
		hasNextPage,
		setLoading,
		resourceId,
		setPage,
		setFetchedPages,
		setHasNextPage,
	]);
	const { oldVersionHistoryAccess } = useSuspenseFeatureAccess();

	const { mutate: revertToHistory, isPending: isReverting } =
		useApiRevertToHistory({
			onSuccess: () => {
				// Invalidate the entity query to trigger a refetch with the reverted data
				queryClient.invalidateQueries({
					queryKey: secodaEntitiesQueryKeyFactory.byId(resourceId),
				});

				showNotification({
					title: 'Success',
					message: 'Version history restored successfully.',
					color: 'green',
				});
			},
			onError: () => {
				showNotification({
					title: 'Error',
					message:
						'Something went wrong while restoring the version history. Please try again later.',
					color: 'red',
				});
			},
		});

	const handleRestoreVersion = useCallback(() => {
		if (!selectedId) {
			return;
		}

		revertToHistory({
			queryParams: {
				id: selectedId,
			},
		});

		trackEvent('resource_log/version_history/restore', {
			resource_id: resourceId,
			version_history_id: selectedId,
		});

		close();
	}, [revertToHistory, selectedId, resourceId, close]);

	useEffect(() => {
		if (page === 1) {
			loadMore();
		}
	}, []);

	useEffect(() => {
		if (!initialSelectedId && versionHistories?.length) {
			setSelectedId(versionHistories[0].id);
		}
	}, [initialSelectedId, versionHistories]);

	const itemContent = useCallback(
		(index: number, item: VersionHistoryOut) => (
			<VersionHistoryButton
				key={item.id}
				versionHistory={item}
				selected={selectedId === item.id}
				onClick={() => setSelectedId(item.id)}
			/>
		),
		[selectedId, setSelectedId]
	);

	if (!versionHistories) {
		return null;
	}

	const selectedVersionHistory = versionHistories.find(
		(versionHistory) => versionHistory.id === selectedId
	);

	if (!selectedVersionHistory) {
		return null;
	}

	// Legacy version histories only support restoring the definition. We don't have any other historical information from them.
	const isLegacy = selectedVersionHistory.is_legacy;
	const snapshot =
		selectedVersionHistory.metadata.secoda_entity_property_snapshot;

	const title =
		selectedVersionHistory.metadata.current.title ?? snapshot?.title;
	const description =
		selectedVersionHistory.metadata.description_diff.markdown ??
		selectedVersionHistory.metadata.current.description ??
		snapshot?.description;
	const isOlderThan30Days = !!selectedVersionHistory.older_than_thirty_days;
	const displayLockedIcon = isOlderThan30Days && !oldVersionHistoryAccess;
	const leftColumn = displayLockedIcon ? (
		<SnapshotModalContentUpgrade />
	) : (
		<Stack spacing="2xl">
			{(title || description) && (
				<Group pt="xl" pb="xs" spacing="xs">
					<Stack spacing="md">
						{title && <Title size="xl">{title}</Title>}
						{description && <RichEditor readOnly value={description} />}
					</Stack>
				</Group>
			)}
			<SnapshotProperties versionHistory={selectedVersionHistory} />
			{isLegacy && (
				<Banner
					message="This is a legacy version history. We only have information about the document."
					tone="info"
					inCard
				/>
			)}
			<Stack spacing="sm">
				<Text size="sm" color="text/secondary/default">
					Documentation
				</Text>
				<RichEditor
					readOnly
					value={
						selectedVersionHistory.metadata.definition_diff.markdown ??
						snapshot?.definition ??
						selectedVersionHistory.metadata.current.definition ??
						''
					}
				/>
			</Stack>
		</Stack>
	);

	return (
		<>
			<SnapshotModalBody
				leftColumn={leftColumn}
				rightColumn={
					<Container fluid w="100%" h="100%" pt="sm" pl="sm" pr="0">
						<Virtuoso
							height={'calc(100vh - 5dvh - 5dvh - 54 - 60 - 1)px'}
							data={versionHistories}
							endReached={loadMore}
							itemContent={itemContent}
						/>
					</Container>
				}
				actions={[
					<Button key="cancel" variant="default" onClick={onClose}>
						Cancel
					</Button>,
					<Button
						key="restore"
						variant="primary"
						onClick={open}
						disabled={displayLockedIcon}
					>
						Restore version
					</Button>,
				]}
			/>
			{opened && (
				<VersionHistoryRestoreModal
					handleRestoreVersion={handleRestoreVersion}
					isLoading={isReverting}
					onClose={close}
				/>
			)}
		</>
	);
}
